import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import config from "@/config.json";

export default function ContactPage() {
  const contactItems = [
    {
      icon: Phone,
      label: "Phone",
      value: config.contact.phone,
      href: `tel:${config.contact.phone}`,
    },
    {
      icon: Mail,
      label: "Email",
      value: config.contact.email,
      href: `mailto:${config.contact.email}`,
    },
    {
      icon: MapPin,
      label: "Address",
      value: config.contact.address,
      href: null,
    },
    {
      icon: Clock,
      label: "Hours",
      value: config.contact.hours,
      href: null,
    },
  ];

  return (
    <main data-testid="contact-page" className="pt-20 bg-[#FDFCF8]">
      {/* Page Header */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F4F1EA]">
        <div className="max-w-[1600px] mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059] mb-4 font-body">
            Reach Out
          </p>
          <h1 className="font-heading text-5xl md:text-7xl font-medium tracking-tight text-[#1A1A1A] mb-6">
            Contact Us
          </h1>
          <p className="text-base md:text-lg text-[#595959] leading-relaxed font-body max-w-xl mx-auto">
            We would love to hear from you. Visit our showroom or reach out through any of the channels below.
          </p>
        </div>
      </section>

      {/* Contact Info + Map */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Contact Details */}
            <div data-testid="contact-details" className="space-y-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#1A1A1A] mb-2">
                  Get in Touch
                </h2>
                <p className="text-sm text-[#595959] font-body leading-relaxed">
                  Whether you have a question about our collection or want to visit our showroom, we are here to help.
                </p>
              </div>

              <Separator className="bg-[#E5E5E5]" />

              <div className="space-y-8">
                {contactItems.map((item, i) => {
                  const Icon = item.icon;
                  const Content = (
                    <div className="flex items-start gap-5 group" key={i}>
                      <div className="flex items-center justify-center w-12 h-12 border border-[#E5E5E5] text-[#C5A059] group-hover:border-[#C5A059] transition-colors duration-300 shrink-0">
                        <Icon size={18} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#C5A059] mb-1 font-body">
                          {item.label}
                        </p>
                        <p className="text-base text-[#1A1A1A] font-body leading-relaxed">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  );

                  if (item.href) {
                    return (
                      <a
                        key={i}
                        href={item.href}
                        data-testid={`contact-${item.label.toLowerCase()}`}
                        className="block hover:translate-x-1 transition-transform duration-300"
                      >
                        {Content}
                      </a>
                    );
                  }
                  return (
                    <div key={i} data-testid={`contact-${item.label.toLowerCase()}`}>
                      {Content}
                    </div>
                  );
                })}
              </div>

              <Separator className="bg-[#E5E5E5]" />

              {/* WhatsApp CTA */}
              {config.social.whatsapp && (
                <a
                  href={config.social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-cta"
                  className="inline-block rounded-none border border-[#1A1A1A] bg-[#1A1A1A] text-white px-8 py-3 hover:bg-transparent hover:text-[#1A1A1A] transition-all duration-300 uppercase tracking-[0.2em] text-xs font-bold"
                >
                  Message on WhatsApp
                </a>
              )}
            </div>

            {/* Google Map */}
            <div data-testid="map-container" className="space-y-6">
              <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#1A1A1A]">
                Find Us
              </h2>
              <div className="w-full aspect-square lg:aspect-[4/5] overflow-hidden border border-[#E5E5E5]">
                <iframe
                  data-testid="google-map-embed"
                  src={config.map.embed_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Store Location"
                  className="w-full h-full"
                />
              </div>
              <p className="text-sm text-[#595959] font-body">
                {config.contact.address}
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
